import { useNavigate, useParams } from 'react-router-dom';
import { RefreshCw, Shield, CheckCircle, AlertTriangle, Clock, GitCommit, FolderOpen } from 'lucide-react';
import { motion, AnimatePresence } from 'motion/react';
import { useQuery } from '@tanstack/react-query';
import { apiClient } from '../api/client';
import { useProjects } from '../hooks/useProjects';
import { Card, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';

interface Scan {
  id: string;
  project_id: string;
  source: string;
  commit_sha?: string;
  branch?: string;
  findings_count: number;
  created_at: string;
  created_by?: string;
}

interface ScansResponse {
  scans: Scan[];
}

function formatTimeAgo(dateStr: string): string {
  const date = new Date(dateStr);
  const now = new Date();
  const diffMs = now.getTime() - date.getTime();
  const diffSecs = Math.floor(diffMs / 1000);
  const diffMins = Math.floor(diffSecs / 60);
  const diffHours = Math.floor(diffMins / 60);
  const diffDays = Math.floor(diffHours / 24);

  if (diffDays > 0) {
    return diffDays === 1 ? '1 day ago' : `${diffDays} days ago`;
  }
  if (diffHours > 0) {
    return diffHours === 1 ? '1 hour ago' : `${diffHours} hours ago`;
  }
  if (diffMins > 0) {
    return diffMins === 1 ? '1 minute ago' : `${diffMins} minutes ago`;
  }
  return 'Just now';
}

function getSourceLabel(source: string): string {
  switch (source) {
    case 'cli':
      return 'CLI';
    case 'action':
      return 'GitHub Action';
    case 'app':
      return 'Dashboard';
    default:
      return source;
  }
}

export function ScansPage() {
  const { projectId } = useParams<{ projectId: string }>();
  const navigate = useNavigate();
  const { selectedProject } = useProjects();
  const actualProjectId = projectId || selectedProject?.id || null;

  // Debug logging
  console.log('[ScansPage] projectId from URL:', projectId);
  console.log('[ScansPage] selectedProject:', selectedProject?.id);
  console.log('[ScansPage] actualProjectId:', actualProjectId);

  const {
    data,
    isLoading,
    isFetching,
    refetch,
    error,
  } = useQuery({
    queryKey: ['scans', actualProjectId],
    queryFn: async () => {
      console.log('[ScansPage] Fetching scans for project:', actualProjectId);
      const response = await apiClient.get<ScansResponse>(`/api/v1/projects/${actualProjectId}/scans`);
      console.log('[ScansPage] API response:', response.data);
      return response.data;
    },
    enabled: !!actualProjectId,
    staleTime: 1000 * 30,
  });

  // Debug: log query state
  console.log('[ScansPage] Query state - isLoading:', isLoading, 'isFetching:', isFetching, 'error:', error);

  const scans = data?.scans ?? [];

  if (!actualProjectId) {
    return (
      <div className="max-w-6xl mx-auto">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.5 }}
        >
          <Card className="border-border/50 bg-card/50 backdrop-blur-sm">
            <CardContent className="p-12 text-center">
              <div className="inline-flex items-center justify-center w-12 h-12 rounded-xl bg-muted/50 mb-4">
                <FolderOpen className="h-6 w-6 text-muted-foreground" />
              </div>
              <p className="text-muted-foreground">
                Select a project from the sidebar to view scans
              </p>
            </CardContent>
          </Card>
        </motion.div>
      </div>
    );
  }

  return (
    <motion.div
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      transition={{ duration: 0.4 }}
      className="max-w-6xl mx-auto"
    >
      <motion.div
        initial={{ opacity: 0, y: -10 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.3 }}
        className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4 mb-6"
      >
        <div>
          <h1 className="text-2xl font-bold text-foreground tracking-tight">Secret Scans</h1>
          <p className="text-sm text-muted-foreground mt-1">
            View secret scanning results from CI/CD and CLI
          </p>
        </div>

        <div className="flex items-center gap-2">
          <div className="flex items-center gap-1 p-1 rounded-lg bg-muted/30">
            <Button
              variant="ghost"
              size="icon-sm"
              onClick={() => refetch()}
              disabled={isFetching}
              title="Refresh"
              className="hover:bg-muted/50"
            >
              <RefreshCw className={`h-3.5 w-3.5 ${isFetching ? 'animate-spin' : ''}`} />
            </Button>
          </div>
        </div>
      </motion.div>

      <motion.div
        initial={{ opacity: 0, y: 10 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.3, delay: 0.1 }}
      >
        <Card className="glass border-primary/20 mb-6">
          <CardContent className="p-5">
            <div className="flex gap-3">
              <div className="flex-shrink-0 mt-0.5 p-2 rounded-lg bg-primary/10">
                <Shield className="h-4 w-4 text-primary" />
              </div>
              <div className="text-sm">
                <p className="font-medium text-foreground">About Secret Scanning</p>
                <p className="mt-1 text-muted-foreground">
                  Secret scanning detects accidentally committed secrets in your codebase.
                  Scans are triggered automatically via GitHub Actions or manually via the CLI.
                </p>
              </div>
            </div>
          </CardContent>
        </Card>
      </motion.div>

      <AnimatePresence mode="wait">
        {isLoading ? (
          <motion.div
            key="loading"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
          >
            <Card className="glass overflow-hidden">
              <CardContent className="p-0">
                {[1, 2, 3, 4, 5].map((i) => (
                  <div key={i} className="p-5 border-b border-border/50 last:border-b-0">
                    <div className="flex items-start justify-between">
                      <div className="flex-1">
                        <div className="flex items-center gap-3">
                          <div className="h-10 w-10 rounded-lg bg-muted/50 animate-shimmer" />
                          <div className="space-y-2">
                            <div className="h-4 w-32 bg-muted/50 rounded animate-shimmer" />
                            <div className="h-3 w-20 bg-muted/30 rounded animate-shimmer" />
                          </div>
                        </div>
                        <div className="mt-4 flex gap-4">
                          <div className="h-3 w-24 bg-muted/30 rounded animate-shimmer" />
                          <div className="h-3 w-32 bg-muted/30 rounded animate-shimmer" />
                        </div>
                      </div>
                    </div>
                  </div>
                ))}
              </CardContent>
            </Card>
          </motion.div>
        ) : scans.length === 0 ? (
          <motion.div
            key="empty"
            initial={{ opacity: 0, scale: 0.95 }}
            animate={{ opacity: 1, scale: 1 }}
            exit={{ opacity: 0, scale: 0.95 }}
            transition={{ duration: 0.3 }}
          >
            <Card className="glass overflow-hidden">
              <CardContent className="py-16 text-center">
                <motion.div
                  initial={{ scale: 0 }}
                  animate={{ scale: 1 }}
                  transition={{ type: 'spring', stiffness: 300, damping: 20, delay: 0.1 }}
                  className="inline-flex items-center justify-center w-16 h-16 rounded-2xl bg-muted/30 mb-4"
                >
                  <Shield className="h-8 w-8 text-muted-foreground/50" />
                </motion.div>
                <motion.h3
                  initial={{ opacity: 0, y: 10 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ delay: 0.2 }}
                  className="text-lg font-medium text-foreground"
                >
                  No scans yet
                </motion.h3>
                <motion.p
                  initial={{ opacity: 0, y: 10 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ delay: 0.25 }}
                  className="mt-2 text-sm text-muted-foreground max-w-sm mx-auto"
                >
                  Run your first secret scan using the CLI or set up the GitHub Action
                  to scan your repository automatically.
                </motion.p>
              </CardContent>
            </Card>
          </motion.div>
        ) : (
          <motion.div
            key="scans"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
          >
            <Card className="glass overflow-hidden">
              <div className="divide-y divide-border/50">
                {scans.map((scan, index) => {
                  const hasFindings = scan.findings_count > 0;

                  return (
                    <motion.div
                      key={scan.id}
                      initial={{ opacity: 0, y: 20 }}
                      animate={{ opacity: 1, y: 0 }}
                      transition={{
                        duration: 0.3,
                        delay: index * 0.05,
                        ease: [0.25, 0.46, 0.45, 0.94],
                      }}
                      whileHover={{ backgroundColor: 'hsl(var(--muted) / 0.1)' }}
                      className="p-5 cursor-pointer transition-colors"
                      onClick={() => navigate(`/projects/${actualProjectId}/scans/${scan.id}`)}
                    >
                      <div className="flex items-start justify-between">
                        <div className="flex-1">
                          <div className="flex items-center gap-3">
                            <motion.div
                              whileHover={{ scale: 1.05 }}
                              whileTap={{ scale: 0.95 }}
                              className={`p-2.5 rounded-lg ${
                                hasFindings
                                  ? 'bg-orange-500/10'
                                  : 'bg-green-500/10'
                              }`}
                            >
                              {hasFindings ? (
                                <AlertTriangle className="h-4 w-4 text-orange-500" />
                              ) : (
                                <CheckCircle className="h-4 w-4 text-green-500" />
                              )}
                            </motion.div>
                            <div>
                              <h3 className="text-base font-medium text-foreground">
                                {getSourceLabel(scan.source)}
                              </h3>
                              <div className="flex items-center gap-2 mt-1">
                                {scan.commit_sha && (
                                  <span className="flex items-center gap-1 text-xs text-muted-foreground font-mono">
                                    <GitCommit className="h-3 w-3" />
                                    {scan.commit_sha.slice(0, 7)}
                                  </span>
                                )}
                                {scan.branch && (
                                  <span className="text-xs text-muted-foreground">
                                    on {scan.branch}
                                  </span>
                                )}
                              </div>
                            </div>
                          </div>

                          <div className="mt-3 flex flex-wrap gap-4 text-sm">
                            <div className="flex items-center gap-1.5 text-muted-foreground">
                              <Shield className="h-3.5 w-3.5" />
                              <span>
                                {scan.findings_count === 0 ? (
                                  <span className="text-green-600">No findings</span>
                                ) : (
                                  <span className={hasFindings ? 'text-orange-600' : ''}>
                                    {scan.findings_count} {scan.findings_count === 1 ? 'finding' : 'findings'}
                                  </span>
                                )}
                              </span>
                            </div>
                            <div className="flex items-center gap-1.5 text-muted-foreground">
                              <Clock className="h-3.5 w-3.5" />
                              <span>{formatTimeAgo(scan.created_at)}</span>
                            </div>
                          </div>
                        </div>
                      </div>
                    </motion.div>
                  );
                })}
              </div>
            </Card>
          </motion.div>
        )}
      </AnimatePresence>
    </motion.div>
  );
}
